import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Settings() {
  const { currentWorkspace, refresh } = useAuth();
  const [ws, setWs] = useState(currentWorkspace);
  const [members, setMembers] = useState([]);
  const [invite, setInvite] = useState({ email: "", name: "", role: "agency_admin" });

  useEffect(() => { setWs(currentWorkspace); }, [currentWorkspace]);
  useEffect(() => { if (currentWorkspace) api.get(`/workspaces/${currentWorkspace.id}/members`).then((r) => setMembers(r.data)); }, [currentWorkspace]);

  const save = async () => {
    await api.patch(`/workspaces/${ws.id}`, {
      name: ws.name, brand_name: ws.brand_name, brand_tagline: ws.brand_tagline,
      accent_color: ws.accent_color, pillar_allocation: ws.pillar_allocation,
      monthly_feed_target: ws.monthly_feed_target,
    });
    toast.success("Saved");
    refresh();
  };

  const sendInvite = async () => {
    if (!invite.email) return;
    try {
      const { data } = await api.post(`/workspaces/${ws.id}/members`, invite);
      toast.success(`Invited ${invite.email}${data.temp_password ? ` · temp password: ${data.temp_password}` : ""}`);
      setInvite({ email: "", name: "", role: "agency_admin" });
      const r = await api.get(`/workspaces/${ws.id}/members`); setMembers(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    }
  };

  if (!ws) return <div className="p-8">Loading…</div>;
  const total = Object.values(ws.pillar_allocation || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="p-8 max-w-[900px]">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Settings</div>
        <h1 className="text-5xl font-black tracking-tight leading-none" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Workspace</h1>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Brand</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Workspace name" value={ws.name} onChange={(v) => setWs({ ...ws, name: v })} testid="ws-name" />
          <Field label="Brand name" value={ws.brand_name} onChange={(v) => setWs({ ...ws, brand_name: v })} testid="brand-name" />
          <Field label="Tagline" value={ws.brand_tagline} onChange={(v) => setWs({ ...ws, brand_tagline: v })} testid="brand-tagline" />
          <Field label="Monthly feed target" type="number" value={ws.monthly_feed_target} onChange={(v) => setWs({ ...ws, monthly_feed_target: parseInt(v) })} testid="feed-target" />
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Content pillar allocation</h2>
        <div className="text-xs text-neutral-500 mb-2">Total must equal 100% (currently {total}%)</div>
        {Object.entries(ws.pillar_allocation || {}).map(([k, v]) => (
          <div key={k} className="flex items-center gap-3 mb-2">
            <div className="w-40 text-sm">{k.replace(/_/g, " ")}</div>
            <input data-testid={`pillar-${k}`} type="number" value={v} onChange={(e) => setWs({ ...ws, pillar_allocation: { ...ws.pillar_allocation, [k]: parseFloat(e.target.value) } })} className="border border-neutral-300 rounded px-2 py-1 text-sm w-24" />
            <div className="text-sm text-neutral-500">%</div>
          </div>
        ))}
      </div>

      <button data-testid="save-settings-btn" onClick={save} disabled={total !== 100} className="px-4 py-2 rounded font-semibold text-sm text-neutral-900 disabled:opacity-50 mb-8" style={{ background: "#D4FF00" }}>Save changes</button>

      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <h2 className="text-lg font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Team</h2>
        <div className="mb-4">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between text-sm border-b border-neutral-100 py-2">
              <div><span className="font-semibold">{m.name}</span> · {m.email}</div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{m.role}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <input data-testid="invite-name" placeholder="Name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="border border-neutral-300 rounded px-3 py-2 text-sm" />
          <input data-testid="invite-email" placeholder="Email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="border border-neutral-300 rounded px-3 py-2 text-sm flex-1 min-w-[200px]" />
          <select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className="border border-neutral-300 rounded px-3 py-2 text-sm">
            <option value="agency_admin">Agency Admin</option>
            <option value="agency_contributor">Agency Contributor</option>
          </select>
          <button data-testid="send-invite-btn" onClick={sendInvite} className="px-4 py-2 rounded font-semibold text-sm text-neutral-900" style={{ background: "#D4FF00" }}>Invite</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">{label}</div>
      <input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-neutral-300 rounded px-2 py-1.5 text-sm" />
    </label>
  );
}
